import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { FileText, CheckCircle, AlertCircle, RefreshCw, Clock, Building2 } from 'lucide-react';

const API = `${window.location.origin}/api`;

const InvoiceRequestForm = () => {
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    tin: '',
    id_type: 'NRIC',
    id_value: '',
    sst_no: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'MYS',
    email: '',
    company_name: ''
  });

  const malaysiaStates = [
    'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 
    'Pahang', 'Pulau Pinang', 'Perak', 'Perlis', 'Selangor', 
    'Terengganu', 'Sabah', 'Sarawak', 'Kuala Lumpur', 'Labuan', 'Putrajaya'
  ];

  const idTypes = [
    { value: 'NRIC', label: 'NRIC (Malaysian IC)' },
    { value: 'PASSPORT', label: 'Passport' },
    { value: 'BRN', label: 'Business Registration Number' },
    { value: 'ARMY', label: 'Army ID' }
  ];

  useEffect(() => {
    fetchRequest();
  }, [token]);

  const fetchRequest = async () => {
    try {
      const res = await axios.get(`${API}/einvoice/invoice-requests/${token}`);
      setRequest(res.data);
      
      // Pre-fill form with existing data
      if (res.data.guest_name) {
        setFormData(prev => ({
          ...prev,
          full_name: res.data.guest_name,
          email: res.data.guest_email || ''
        }));
      }
      
      if (res.data.status === 'completed') {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error fetching request:', error);
      if (error.response?.status === 404) {
        setRequest({ error: 'not_found' });
      } else {
        setRequest({ error: 'unknown' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name) {
      toast.error('Please enter your full name');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/einvoice/invoice-requests/${token}/submit`, formData);
      setSubmitted(true);
      setInvoiceDetails(res.data);
      toast.success('Invoice request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (request?.error === 'not_found') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Request Not Found</h2>
            <p className="text-slate-600">
              This invoice request link is invalid or has expired. 
              Please contact the hotel for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (request?.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Clock className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Request Expired</h2>
            <p className="text-slate-600">
              This invoice request link has expired. 
              Please contact the hotel for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invoice Request Submitted!</h2>
            <p className="text-slate-600 mb-4">
              Thank you for providing your details. Your e-Invoice will be generated and submitted to LHDN.
            </p>
            {invoiceDetails && (
              <div className="bg-slate-50 rounded-lg p-4 text-left mt-4">
                <p className="text-sm text-slate-500">Invoice Number</p>
                <p className="font-mono font-bold text-blue-600">{invoiceDetails.invoice_number}</p>
              </div>
            )}
            <p className="text-sm text-slate-500 mt-4">
              You will receive the invoice PDF via email once processed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4" data-testid="invoice-request-form">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Building2 className="w-12 h-12 mx-auto text-blue-600 mb-3" />
          <h1 className="text-2xl font-bold text-slate-900">e-Invoice Information</h1>
          <p className="text-slate-600">Please provide your tax details for LHDN e-Invoice compliance</p>
        </div>

        {/* Booking Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Booking Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Guest Name</p>
                <p className="font-medium">{request?.guest_name || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Total Amount</p>
                <p className="font-bold text-blue-600">{formatCurrency(request?.amount)}</p>
              </div>
              <div>
                <p className="text-slate-500">Check-in</p>
                <p className="font-medium">{formatDate(request?.check_in)}</p>
              </div>
              <div>
                <p className="text-slate-500">Check-out</p>
                <p className="font-medium">{formatDate(request?.check_out)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Information</CardTitle>
            <CardDescription>
              This information is required for Malaysia LHDN e-Invoice compliance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal/Company Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Personal / Company Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      placeholder="As per IC/Passport"
                      required
                      data-testid="input-full-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_name">Company Name (if applicable)</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Company Sdn Bhd"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="tin">Tax Identification Number (TIN)</Label>
                    <Input
                      id="tin"
                      value={formData.tin}
                      onChange={(e) => setFormData({...formData, tin: e.target.value})}
                      placeholder="e.g., IG12345678901"
                    />
                    <p className="text-xs text-slate-500 mt-1">Leave blank if not applicable</p>
                  </div>
                  <div>
                    <Label htmlFor="sst_no">SST Registration Number</Label>
                    <Input
                      id="sst_no"
                      value={formData.sst_no}
                      onChange={(e) => setFormData({...formData, sst_no: e.target.value})}
                      placeholder="e.g., B12-1234-56789012"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="id_type">ID Type</Label>
                    <Select 
                      value={formData.id_type} 
                      onValueChange={(v) => setFormData({...formData, id_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {idTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="id_value">{formData.id_type === 'NRIC' ? 'NRIC Number' : 'ID Number'}</Label>
                    <Input
                      id="id_value"
                      value={formData.id_value}
                      onChange={(e) => setFormData({...formData, id_value: e.target.value})}
                      placeholder={formData.id_type === 'NRIC' ? '880101-01-1234' : 'ID Number'}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Contact Information</h3>
                
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@example.com"
                  />
                  <p className="text-xs text-slate-500 mt-1">Invoice PDF will be sent to this email</p>
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="City"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="state">State</Label>
                    <Select 
                      value={formData.state} 
                      onValueChange={(v) => setFormData({...formData, state: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {malaysiaStates.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="postal_code">Postal Code</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      placeholder="50000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      placeholder="MYS"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={submitting}
                  data-testid="submit-request-btn"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Submit Invoice Request
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 text-center mt-3">
                  By submitting, you authorize the hotel to generate an e-Invoice on your behalf
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-500">
          <p>Powered by StayMii e-Invoice Platform</p>
          <p>Malaysia LHDN MyInvois Compliant</p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceRequestForm;
